import { useEffect, useState } from 'react';
import { authApi } from '../auth/api';
import freebeLogo from '../assets/integrations/freebe-logo.webp';
import googleDriveLogo from '../assets/integrations/google-drive-logo.png';
import trelloLogo from '../assets/integrations/trello-logo.png';
import { PrototypeIcon, type PrototypeIconName } from './PrototypeIcon';
import {
  connectorLogoApi,
  type ConnectorId,
  type ConnectorLogoRevision,
} from './connector-logo-api';

const CONNECTORS: readonly {
  id: ConnectorId;
  label: string;
  icon: PrototypeIconName;
  defaultLogo?: string;
}[] = [
  { id: 'freebe', label: 'Freebe', icon: 'coins', defaultLogo: freebeLogo },
  { id: 'bnp', label: 'BNP Paribas', icon: 'briefcase-business' },
  { id: 'trello', label: 'Trello', icon: 'layout-grid', defaultLogo: trelloLogo },
  { id: 'google-drive', label: 'Google Drive', icon: 'database', defaultLogo: googleDriveLogo },
];

/** Réglage global : aucun média de l'organisation n'est écrit dans le bundle applicatif. */
export function ConnectorLogoSettings(): React.JSX.Element {
  const [logos, setLogos] = useState<readonly ConnectorLogoRevision[]>([]);
  const [files, setFiles] = useState<Partial<Record<ConnectorId, File>>>({});
  const [previews, setPreviews] = useState<Partial<Record<ConnectorId, string>>>({});
  const [busy, setBusy] = useState<ConnectorId | null>(null);
  const [notice, setNotice] = useState<{ text: string; kind: 'success' | 'error' } | null>(null);
  useEffect(() => {
    void connectorLogoApi
      .list()
      .then(setLogos)
      .catch(() => setNotice({ text: 'Logos indisponibles pour le moment.', kind: 'error' }));
  }, []);
  useEffect(() => {
    const created = Object.entries(files)
      .filter((entry): entry is [string, File] => entry[1] instanceof File)
      .map(([id, file]) => [id, URL.createObjectURL(file)] as const);
    setPreviews(Object.fromEntries(created));
    return () => {
      for (const [, url] of created) URL.revokeObjectURL(url);
    };
  }, [files]);

  function change(id: ConnectorId, file: File | null): void {
    setNotice(null);
    if (
      file &&
      (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 512 * 1024)
    ) {
      setNotice({
        text: 'Utilisez une image PNG, JPEG ou WebP de 512 Kio maximum.',
        kind: 'error',
      });
      return;
    }
    setFiles((current) => {
      const updated = { ...current };
      if (file) updated[id] = file;
      else delete updated[id];
      return updated;
    });
  }

  async function mutate(id: ConnectorId, operation: 'save' | 'remove'): Promise<void> {
    if (busy !== null) return;
    setBusy(id);
    setNotice(null);
    try {
      const { csrfToken } = await authApi.csrfToken();
      if (operation === 'save') {
        const file = files[id];
        if (!file) return;
        const result = await connectorLogoApi.save(id, file, csrfToken);
        setLogos((current) => [
          ...current.filter((item) => item.connectorId !== id),
          { connectorId: id, revision: result.revision },
        ]);
      } else {
        await connectorLogoApi.remove(id, csrfToken);
        setLogos((current) => current.filter((item) => item.connectorId !== id));
      }
      setFiles((current) => {
        const updated = { ...current };
        delete updated[id];
        return updated;
      });
      setNotice({
        text:
          operation === 'save'
            ? `${id === 'bnp' ? 'BNP Paribas' : id === 'google-drive' ? 'Google Drive' : id === 'freebe' ? 'Freebe' : 'Trello'} : logo enregistré.`
            : 'Logo par défaut rétabli.',
        kind: 'success',
      });
    } catch {
      setNotice({
        text: 'Modification du logo refusée. Vérifiez le fichier et la session.',
        kind: 'error',
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      className="supervisor-panel connector-logo-settings"
      aria-labelledby="connector-logo-settings-title"
    >
      <header className="supervisor-panel__header">
        <div>
          <p className="dashboard-panel__eyebrow">Configuration des connecteurs</p>
          <h2 id="connector-logo-settings-title">Logos des connecteurs</h2>
          <p>
            Préparez vos visuels sur un gabarit de 450 × 200 px. Ils seront affichés en entier, sans
            recadrage, dans Connexions.
          </p>
        </div>
        <PrototypeIcon name="palette" />
      </header>
      {notice ? (
        <p
          className={`connector-logo-settings__notice is-${notice.kind}`}
          role={notice.kind === 'error' ? 'alert' : 'status'}
        >
          <PrototypeIcon name={notice.kind === 'success' ? 'check' : 'circle-help'} />
          {notice.text}
        </p>
      ) : null}
      <div className="connector-logo-settings__grid">
        {CONNECTORS.map(({ id, label, icon, defaultLogo }) => (
          <div
            className={`connector-logo-settings__item connector-logo-settings__item--${id}`}
            key={id}
          >
            <div className="connector-logo-settings__item-heading">
              <span className="connector-logo-settings__icon">
                <PrototypeIcon name={icon} />
              </span>
              <strong>{label}</strong>
              <span className="connector-logo-settings__badge">
                {previews[id]
                  ? 'À enregistrer'
                  : connectorLogoApi.url(id, logos)
                    ? 'Personnalisé'
                    : 'Par défaut'}
              </span>
            </div>
            <div className="connector-logo-settings__preview">
              {previews[id] || connectorLogoApi.url(id, logos) || defaultLogo ? (
                <img
                  src={previews[id] ?? connectorLogoApi.url(id, logos) ?? defaultLogo}
                  alt={`Aperçu du logo ${label}`}
                />
              ) : (
                <span className="connector-logo-settings__default-logo">BNP Paribas</span>
              )}
            </div>
            <label className="connector-logo-settings__file-choice">
              <PrototypeIcon name="cloud-upload" />
              <span>{files[id]?.name ?? 'Choisir un logo'}</span>
              <input
                type="file"
                aria-label={`Choisir un logo pour ${label}`}
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => {
                  change(id, event.target.files?.[0] ?? null);
                  event.target.value = '';
                }}
              />
            </label>
            <small className="connector-logo-settings__hint">
              PNG, JPEG ou WebP · 512 Kio max.
            </small>
            <div className="connector-logo-settings__actions">
              <button
                type="button"
                disabled={!files[id] || busy !== null}
                onClick={() => void mutate(id, 'save')}
              >
                <PrototypeIcon name="pencil" /> Enregistrer
              </button>
              <button
                type="button"
                disabled={!connectorLogoApi.url(id, logos) || busy !== null}
                onClick={() => void mutate(id, 'remove')}
              >
                Rétablir le logo
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
